import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import type { PitcherReadiness } from '../../lib/pitcher-readiness';
import { WizardBar, WizardHead, WizardFoot, WizardList, WizardGroupLabel } from './WizardChrome';

// Step 4. Queue up the first relievers.
//
// Entirely new: `game.plan` stored only startingPitcherId, so there was nowhere
// to record a second or third arm and coaches carried it in their heads.
//
// The coverage bar is arithmetic over an engine that already exists: each arm's
// forecastInnings laid end to end against the scheduled innings. It never says a
// plan is wrong, only which innings are still unaccounted for.

/** Lay each planned arm's forecast end to end. Returns one slot per inning. */
export function buildCoverage(plan: (PitcherReadiness | null)[], numInnings: number) {
  const slots: { inning: number; who: PitcherReadiness | null; slot: number }[] = [];
  let inning = 1;
  plan.forEach((arm, idx) => {
    if (!arm) return;
    // An arm with no logged pitches has no forecast; assume a conservative two.
    const span = Math.max(1, Math.round(arm.forecastIP ?? 2));
    for (let i = 0; i < span && inning <= numInnings; i++) {
      slots.push({ inning, who: arm, slot: idx });
      inning++;
    }
  });
  while (inning <= numInnings) { slots.push({ inning, who: null, slot: -1 }); inning++; }
  return slots;
}

/**
 * Who the bullpen plan puts on the mound, one entry per inning (0-based).
 * Innings past the last planned arm stay with that arm rather than going
 * empty, which is what the defense did before the bullpen step existed, so a
 * coach who plans one reliever never finds a position player on the mound.
 */
export function plannedPitcherByInning(plan: (PitcherReadiness | null)[], numInnings: number): (string | null)[] {
  let last: string | null = null;
  return buildCoverage(plan, numInnings).map(c => {
    if (c.who) last = c.who.player.id;
    return last;
  });
}

const SLOT_LABELS = ['1st', '2nd', '3rd'];
const BAR_TONES = ['bg-accent-2', 'bg-accent', 'bg-accent/60'];

export const StepBullpen = ({
  rows, pitcherPlan, numInnings, stepLabel,
  onSetSlot, onBack, onContinue, onSkip,
}: {
  rows: PitcherReadiness[];
  pitcherPlan: (string | null)[];
  numInnings: number;
  stepLabel: string;
  onSetSlot: (index: number, playerId: string | null) => void;
  onBack: () => void;
  onContinue: () => void;
  onSkip: () => void;
}) => {
  const [picking, setPicking] = useState<number | null>(null);
  const byId = new Map(rows.map(r => [r.player.id, r]));
  const planRows = pitcherPlan.map(id => (id ? byId.get(id) ?? null : null));
  const coverage = buildCoverage(planRows, numInnings);
  const covered = coverage.filter(c => c.who).length;

  const available = rows.filter(
    r => r.status !== 'rest' && !pitcherPlan.includes(r.player.id),
  );

  const sub = covered >= numInnings
    ? `All ${numInnings} innings covered.`
    : covered === 0
      ? 'Queue up the first relievers.'
      : `Covered through inning ${covered}. ${numInnings - covered} still open.`;

  return (
    <>
      <WizardBar label={stepLabel} onBack={onBack} />
      <WizardHead title="Bullpen Plan" sub={sub} />

      <div className="flex-1 overflow-y-auto bg-canvas px-4 pt-4">
        <div className="flex gap-1 mb-4">
          {coverage.map(c => (
            <div key={c.inning} className="flex-1">
              <div
                className={`h-[34px] rounded-lg flex items-center justify-center text-xs font-bold ${
                  c.who
                    ? `${BAR_TONES[c.slot] ?? 'bg-accent'} text-surface`
                    : 'bg-raised text-ink-mute border border-dashed border-hairline'
                }`}
              >
                {c.who ? (c.who.player.name?.[0] ?? '?') : '?'}
              </div>
              <span className="block text-center text-[11.5px] font-semibold text-ink-2 mt-1 tabular-nums">
                {c.inning}
              </span>
            </div>
          ))}
        </div>

        <WizardGroupLabel>Your plan</WizardGroupLabel>
        <WizardList>
          {SLOT_LABELS.map((label, i) => {
            const r = planRows[i];
            return (
              <button
                key={label}
                onClick={() => setPicking(i)}
                className={`w-full flex items-center gap-3 px-4 py-3.5 text-left ${i === SLOT_LABELS.length - 1 ? '' : 'border-b border-hairline'}`}
              >
                <span className={`w-[38px] shrink-0 text-center text-[13px] font-bold ${i === 0 ? 'text-gold-ink' : 'text-ink'}`}>
                  {label}
                </span>
                <span className={`flex-1 min-w-0 text-base truncate ${r ? 'font-semibold text-ink' : 'text-ink-mute'}`}>
                  {r ? r.player.name : 'Choose a pitcher'}
                </span>
                {r && (
                  <span className="text-sm text-ink-2 tabular-nums shrink-0">
                    {r.forecastIP != null ? `~${r.forecastIP.toFixed(1)} IP` : r.statusLabel}
                  </span>
                )}
                <ChevronRight className="w-5 h-5 text-ink-mute shrink-0" />
              </button>
            );
          })}
        </WizardList>

        {available.length > 0 && (
          <>
            <WizardGroupLabel>Also available</WizardGroupLabel>
            <WizardList>
              {available.map((r, i) => (
                <div
                  key={r.player.id}
                  className={`flex items-center gap-3 px-4 py-3 ${i === available.length - 1 ? '' : 'border-b border-hairline'}`}
                >
                  <span className="w-[38px] shrink-0 text-center text-[13px] font-semibold text-ink-mute tabular-nums">
                    #{r.player.number}
                  </span>
                  <span className="flex-1 min-w-0 text-[15px] font-semibold text-ink truncate">{r.player.name}</span>
                  <span className="text-sm text-ink-2 shrink-0">{r.statusLabel}</span>
                </div>
              ))}
            </WizardList>
          </>
        )}
        <div className="h-4" />
      </div>

      <WizardFoot
        primaryLabel="Continue"
        onPrimary={onContinue}
        secondaryLabel="Skip, I will decide in the dugout"
        onSecondary={onSkip}
      />

      {picking != null && (
        <div className="absolute inset-0 z-30 flex flex-col">
          <button className="flex-1 bg-ink/30" onClick={() => setPicking(null)} aria-label="Dismiss" />
          <div className="bg-surface rounded-t-panel px-4 pt-5 pb-5 safe-bottom max-h-[70%] overflow-y-auto">
            <h3 className="text-[19px] font-bold tracking-[-0.02em] text-ink px-1 mb-3">
              {SLOT_LABELS[picking]} pitcher
            </h3>
            <WizardList>
              {rows.filter(r => r.status !== 'rest').map((r, i, arr) => (
                <button
                  key={r.player.id}
                  onClick={() => { onSetSlot(picking, r.player.id); setPicking(null); }}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 text-left ${i === arr.length - 1 ? '' : 'border-b border-hairline'}`}
                >
                  <span className="flex-1 min-w-0 text-base font-semibold text-ink truncate">{r.player.name}</span>
                  <span className="text-sm text-ink-2 shrink-0">
                    {r.forecastIP != null ? `~${r.forecastIP.toFixed(1)} IP` : r.statusLabel}
                  </span>
                </button>
              ))}
            </WizardList>
            {pitcherPlan[picking] && (
              <button
                onClick={() => { onSetSlot(picking, null); setPicking(null); }}
                className="w-full h-[38px] mt-3 rounded-control bg-surface border border-hairline text-ink text-[15px] font-bold"
              >
                Clear this slot
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};
